import { SMSMessageDto } from '@act/shared/data-transfer-objects';
import { Button, FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  Divider,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { smsMessageRequests } from 'redux/requests/sms-message.requests';
import { removeMessageFromState } from 'redux/slices/messagesSlice.slice';
import { unassignSMSMessageSuccess } from 'redux/slices/smsMessagesSlice.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import { z } from 'zod';
import { useToast } from '../app/ReusableToast';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
} from '../dialog-forms/components/SharedDialogHookComponents';
import useUnsavedChangesDialog from '../dialog-forms/hooks/useUnsavedChangesDialog.hook';

export interface AssignTextMessageUniqueProps {
  sms: SMSMessageDto;
  currentMessageId?: number;
}

export type AssignTextMessageDialogProps = GenericDialogProps<boolean> &
  AssignTextMessageUniqueProps;

export type AssignTextMessageDialogCallbackInput = {
  // Empty type
};

const AssignTextMessageFormSchema = z.object({
  memberId: z.string().min(1, 'Please select a member'),
});

export type AssignTextMessageForm = z.infer<typeof AssignTextMessageFormSchema>;

const AssignTextMessageDialog = ({
  sms,
  currentMessageId,
  open,
  setOpen,
}: AssignTextMessageDialogProps) => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const lastSearchedTermRef = useRef<string>('');
  const hasLoadedInitialRef = useRef(false);
  const [membersResponse, isLoadingMembers, getMembers] = useApiCall(
    smsMessageRequests.getMembersForSMSAssignment
  );

  // Initial load: search by phone number when dialog opens
  useEffect(() => {
    if (open && sms?.id && sms?.from && !hasLoadedInitialRef.current) {
      hasLoadedInitialRef.current = true;
      getMembers({ params: { id: sms.id, phoneNumber: sms.from } });
    }
    
    // Reset when dialog closes
    if (!open) {
      hasLoadedInitialRef.current = false;
      lastSearchedTermRef.current = '';
      setInputValue('');
      setDebouncedSearchTerm('');
    }
  }, [open, sms?.id, sms?.from]);

  // Debounce the input value to create the search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(inputValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch members when debounced search term changes
  useEffect(() => {
    if (!open || !sms?.id) return;
    
    const trimmedSearchTerm = debouncedSearchTerm.trim();
    
    // Only fetch if we have a search term and it's different from the last one we searched
    if (trimmedSearchTerm.length > 0 && trimmedSearchTerm !== lastSearchedTermRef.current) {
      lastSearchedTermRef.current = trimmedSearchTerm;
      getMembers({ params: { id: sms.id, searchTerm: trimmedSearchTerm } });
    }
  }, [debouncedSearchTerm, open, sms?.id]);

  const members = membersResponse?.users || [];
  const isReassignFlow = currentMessageId !== undefined;

  const { control, handleSubmit, reset, formState } =
    useForm<AssignTextMessageForm | null>({
      mode: 'onChange',
      resolver: !isReassignFlow
        ? zodResolver(AssignTextMessageFormSchema)
        : null,
      defaultValues: !isReassignFlow
        ? {
            memberId: '',
          }
        : {},
    });

  const [assignResult, isAssigning, assignSMSMessage] = useApiCall(
    smsMessageRequests.assignSMSMessage
  );
  const [unassignedSMSMessage, isUnassigningSMSMessage, unassignSMSMessage] =
    useApiCall(smsMessageRequests.unassignSMSMessage);

  const isSubmitting = isAssigning;

  // Handle successful assignment
  useEffect(() => {
    if (assignResult && !isAssigning) {
      toast('Text message assigned successfully', 'success');

      // Remove the old message from the current member's feed if it exists
      if (currentMessageId) {
        dispatch(removeMessageFromState(currentMessageId));
      }

      reset();
      setOpen(false);
    }
  }, [assignResult, isAssigning, reset, setOpen, currentMessageId, dispatch, toast]);

  const { isDirty } = formState;

  const handleClose = useCallback(
    (_event?: unknown, _reason?: string) => {
      // Prevent closing during submission
      if (isSubmitting) {
        return;
      }

      // Check for unsaved changes and show confirmation dialog if needed
      if (isDirty) {
        void handleUnsavedChanges(() => {
          reset();
          setOpen(false);
        });
      } else {
        reset();
        setOpen(false);
      }
    },
    [handleUnsavedChanges, isDirty, reset, setOpen, isSubmitting]
  );

  const onSubmit = (data: AssignTextMessageForm | null) => {
    if (!data.memberId && isReassignFlow) {
      unassignSMSMessage({
        params: { id: sms?.id },
      });
    } else {
      assignSMSMessage({
        params: { id: sms?.id },
        body: { subjectId: data.memberId },
      });
    }
  };

  useEffect(() => {
    if (unassignedSMSMessage && !isUnassigningSMSMessage) {
      dispatch(unassignSMSMessageSuccess(unassignedSMSMessage));
      toast('Text message unassigned successfully', 'success');
      reset();
      setOpen(false);
    }
  }, [unassignedSMSMessage, isUnassigningSMSMessage, reset, setOpen, dispatch, toast]);

  const memberOptions = useMemo(
    () =>
      members.map(member => ({
        label: member.name,
        value: member.id,
      })),
    [members]
  );

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, value: string, reason: string) => {
      if (reason === 'input') {
        setInputValue(value);
      }
    },
    []
  );

  const formConfig: FormConfig<AssignTextMessageForm> = useMemo(
    () => [
      {
        type: 'input',
        name: 'memberId',
        label: isReassignFlow
          ? 'Search for a member (optional)'
          : 'Search for a member',
        input: 'autocomplete',
        props: {
          options: memberOptions,
          required: isReassignFlow ? false : true,
          fullWidth: true,
          placeholder: 'Type to search members...',
          getOptionLabel: option => option.label || '',
          trackingName: 'member-autocomplete',
          sensitive: true,
          onInputChange: handleInputChange,
          disableFiltering: true,
          loading: isLoadingMembers,
        },
      },
    ],
    [memberOptions, isReassignFlow, handleInputChange, isLoadingMembers]
  );

  return (
    <div style={{ width: '600px' }}>
      <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogContentContainer>
          <DialogTitleBarComponent
            title={
              isReassignFlow
                ? 'Reassign or unassign text message'
                : 'Assign text message'
            }
            handleClose={handleClose}
          />
          <form onSubmit={handleSubmit(onSubmit)}>
            <CardContent sx={{ padding: '12px 24px 0' }}>
              <Typography
                variant="titleMedium"
                sx={{ display: 'block', marginBottom: '16px' }}
              >
                {isReassignFlow
                  ? 'Reassign this text message to another member, or just unassign it'
                  : 'Search and select a member for this text message'}
              </Typography>
              <FormBuilder
                config={formConfig}
                control={control}
                formState={formState}
                disabled={isSubmitting}
              />
            </CardContent>
            <Divider sx={{ marginTop: '48px' }} />
            <CardActions
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                padding: '8px 24px',
              }}
            >
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button trackingName="close-button" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button trackingName="assign-text-message-dialog-button-submit"
                  disabled={isSubmitting}
                  debounced
                  type="submit"
                  variant="contained"
                  startIcon={
                    isSubmitting ? <CircularProgress size={20} /> : null
                  }
                >
                  {isSubmitting
                    ? 'Assigning...'
                    : isReassignFlow
                      ? 'Reassign message'
                      : 'Assign message'}
                </Button>
              </div>
            </CardActions>
          </form>
        </DialogContentContainer>
      </Dialog>
    </div>
  );
};

export default AssignTextMessageDialog;
